import apiServices from 'config/apiServiceApiGateway'
import { Get as GetMonolith } from 'config/apiServiceConfig'
import type { ProductModifierType } from 'features/Payment/@types/refund'

const { Get, Post } = apiServices

export type RefundableProductType = {
  product_id: number
  product_name: string
  product_stock: number
  max_refundable_qty: number
  sku_number: string
  product_modifier?: ProductModifierType
}

export type RefundMethodApprovalType = {
  key: string
  value: string
}

export type GetRefundableProductsResponseType = {
  data: {
    order_id: number
    refundable_products: RefundableProductType[]
    is_expired: boolean
    expiry_notes: string
    expiry_complaint_reason_id: number
    disable_return_goods: boolean
    refund_method_approval: RefundMethodApprovalType[]
    disable_refund_proof: boolean
    user_level: string
    refund_proof_msg: string
  }
  error: {
    status: boolean
    message: string
    code: number
  }
  pagination: null
}

export type GetRefundableProductsRequestType = {
  invoice: string | number
}

export type RefundableLineItemType = {
  product_id: number
  product_name: string
  product_stock: number
  product_image: string
  max_refundable_qty: number
  sku_number: string
  product_modifier: ProductModifierType
}

export type GetRefundableProductsResponseNewType = {
  data: {
    order_id: number
    available_refund_line_items: RefundableLineItemType[]
    is_expired: boolean
    expiry_notes: string
    expiry_complaint_reason_id: number
    disable_return_goods: boolean
    refund_methods_approval: RefundMethodApprovalType[]
    disable_refund_proof: boolean
    user_level: string
    refund_proof_msg: string
  }
  error: {
    status: boolean
    message: string
    code: number
  }
}

export type RefundType = {
  refund_insight: string
  order_id: number
  location_fmt: string
  type: string
  total_refund_items: number
  generated_by: string
  refund_method: string
  complaint_reason_fmt: string
  total_refund_loyalty: number
  total_refund_astro: number
  total_refund: number
  items_refund_amount: number
  status_fmt: string
  location_id: number
  created_at: string
  id: number
  created_by: string
  status: string
  customer_id: number
  shipping_refund_amount: number
  notes: string
  refund_method_fmt: string
  complaint_reason_id: number
  transaction_id: string
  reject_reason: string
  customer_name: string
}

export type GetRefundListResponseType = {
  data: {
    refunds: RefundType[]
  }
  pagination: {
    page_index: number
    direction: string
    number_of_elements: number
    sort: string
    total_pages: number
    page_size: number
    total_elements: number
  }
}

export type RefundDetailLineItemType = {
  product_id: number
  product_name: string
  product_price: number
  product_stock: number
  order_id: number
  order_line_item_id: number
  qty_refund: number
  sku_number: string
  modifier_hash_id: number
  is_reverse_pickup: boolean
  product_modifier?: ProductModifierType
}

export type RefundDetailProofType = {
  id: string | number
  file_url: string
}

export type GetRefundDetailResponseType = {
  data: {
    refund_detail: RefundType
    refund_line_items: RefundDetailLineItemType[]
    refund_proofs: RefundDetailProofType[]
  }
  error: {
    status: boolean
    message: string
    code: number
  }
}

export type GetRefundListRequestType = {
  params: {
    page: number
    limit: number
    end_time_unix?: string
    start_time_unix?: string
    order_id?: number
    location_id?: number
    status?: string
    customer_id?: string
    generated_by?: string
  }
}

export const getRefundList = ({ params }: GetRefundListRequestType) =>
  Get<GetRefundListResponseType, true>({
    url: `/payment/internal/v1/refund/list`,
    params,
  })

export const getRefundableProducts = (params: GetRefundableProductsRequestType) =>
  GetMonolith<GetRefundableProductsResponseType>({
    url: `/api/refund/refundable-products`,
    params,
  })

export const getRefundableProductsV2 = (params: GetRefundableProductsRequestType) =>
  Get<GetRefundableProductsResponseNewType, true>({
    url: `/payment/internal/v1/refund/refundable-products`,
    params,
  })

export const getRefundDetailV2 = (id: string | number) =>
  Get<GetRefundDetailResponseType, true>({
    url: `/payment/internal/v1/refund/detail/${id}`,
  })

export type CreateRefundLineItemType = {
  order_id: number | string
  product_id: number | string
  qty_refund: number
  modifier_hash_id: number | string
}

export type PostCreateRefundRequestType = {
  order_id: number | string
  notes: string
  refund_method: string
  refund_proofs: {
    path: string
  }[]
  refund_line_items: CreateRefundLineItemType[]
  complaint_reason_id?: number | string
  generated_by: string
}

export type PostCreateRefundResponseType = {
  data: unknown
  error: {
    status: boolean
    message: string
    code: number
  }
}

export const postCreateRefundV2 = (payload: PostCreateRefundRequestType) =>
  Post<PostCreateRefundResponseType, true>({
    url: `/payment/internal/v1/refund/create`,
    data: payload,
  })
